/** "When it runs" - the ONE schedule editor of Insights (item 63's, shared with
 *  the templates' editor in item 70): how often (daily / weekly / monthly / by
 *  hand), the days, the day of the month, the hour in the owner's own time
 *  zone, and the next runs as the server computes them. The server's rule is
 *  report_schedule.py for both; what a run covers and the honest timing line
 *  are the caller's words. */
import type { ReactNode } from 'react';
import { InputNumber, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { useLanguage } from '@/hooks/useLanguage';
import type { ReportFrequency } from '../../types/insights';
import { instantLabel, weekOrder, weekdayName } from './reportParts';
import './Reports.css';

const FREQUENCIES: ReportFrequency[] = ['daily', 'weekly', 'monthly', 'none'];

export interface ScheduleValue {
  frequency: ReportFrequency;
  /** 0 = Monday … 6 = Sunday. Daily: the days it runs (empty = every day); weekly: one. */
  weekdays: number[];
  monthDay: number;
  /** 'HH:mm', in steps of five minutes. */
  runTime: string;
}

export interface UpcomingRun { at: string }

interface Props<R extends UpcomingRun> {
  value: ScheduleValue;
  onChange: (next: ScheduleValue) => void;
  /** The server's preview: the owner's zone and the next runs. */
  zone?: string | null;
  nextRuns?: R[];
  /** Ids the fields' labels point at (two editors may be open in one page's history). */
  idPrefix: string;
  /** Heading of the next runs ("Next reports", "Next runs"). */
  nextRunsLabel: string;
  /** What one run covers, beside its time (a report's days). */
  runDetail?: (run: R) => ReactNode;
  /** The honest line under the next runs (when it really starts). */
  timingNote: ReactNode;
  /** The line shown instead when it runs only by hand. */
  byHandNote: ReactNode;
  /** The "by hand" choice's name, when the caller words it differently. */
  byHandLabel?: string;
}

/** The weekdays a new frequency starts from (weekly keeps one day; a daily
    choice that was one weekly day becomes every day). */
function withFrequency(value: ScheduleValue, frequency: ReportFrequency): ScheduleValue {
  return {
    ...value,
    frequency,
    weekdays: frequency === 'weekly' ? value.weekdays.slice(0, 1)
      : frequency === 'daily' && value.weekdays.length === 1 ? [] : value.weekdays,
  };
}

/** What the server stores for a schedule (the API's field names). */
export function scheduleFields(value: ScheduleValue): {
  frequency: ReportFrequency; weekdays: number[]; month_day: number | null; run_time: string;
} {
  return {
    frequency: value.frequency,
    weekdays: value.frequency === 'weekly' ? value.weekdays.slice(0, 1)
      : value.frequency === 'daily' ? value.weekdays : [],
    month_day: value.frequency === 'monthly' ? value.monthDay : null,
    run_time: value.runTime,
  };
}

export function ScheduleFields<R extends UpcomingRun>({
  value, onChange, zone, nextRuns, idPrefix, nextRunsLabel, runDetail, timingNote, byHandNote, byHandLabel,
}: Props<R>) {
  const { t, language } = useLanguage();
  const set = (patch: Partial<ScheduleValue>) => onChange({ ...value, ...patch });

  const toggleDay = (day: number) => {
    if (value.frequency === 'weekly') {
      set({ weekdays: [day] });
      return;
    }
    if (!value.weekdays.length) {   // every day: this click leaves one out
      set({ weekdays: weekOrder(language).filter((d) => d !== day) });
      return;
    }
    set({ weekdays: value.weekdays.includes(day) ? value.weekdays.filter((d) => d !== day) : [...value.weekdays, day] });
  };

  return (
    <div className="rp-form" data-testid={`${idPrefix}-schedule-fields`}>
      <div className="rp-choices" role="radiogroup" aria-label={t('insights.reports.editor.frequency')}>
        {FREQUENCIES.map((f) => (
          <button key={f} type="button" role="radio" aria-checked={value.frequency === f} className="rp-choice"
                  onClick={() => onChange(withFrequency(value, f))} data-testid={`${idPrefix}-frequency-${f}`}>
            {f === 'none' && byHandLabel ? byHandLabel : t(`insights.reports.frequency.${f}`)}
          </button>
        ))}
      </div>

      {(value.frequency === 'daily' || value.frequency === 'weekly') && (
        <div>
          <span className="rp-label" id={`${idPrefix}-days-label`}>
            {t(value.frequency === 'weekly' ? 'insights.reports.editor.day' : 'insights.reports.editor.days')}
          </span>
          <div className="rp-days" role={value.frequency === 'weekly' ? 'radiogroup' : 'group'}
               aria-labelledby={`${idPrefix}-days-label`}>
            {weekOrder(language).map((day) => {
              const on = value.frequency === 'daily' && !value.weekdays.length ? true : value.weekdays.includes(day);
              return (
                <button key={day} type="button" className="rp-day"
                        {...(value.frequency === 'weekly' ? { role: 'radio', 'aria-checked': on } : { 'aria-pressed': on })}
                        aria-label={weekdayName(day, 'long')} onClick={() => toggleDay(day)}>
                  {weekdayName(day)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.frequency === 'monthly' && (
        <div>
          <label className="rp-label" htmlFor={`${idPrefix}-month-day`}>{t('insights.reports.editor.monthDay')}</label>
          <InputNumber id={`${idPrefix}-month-day`} min={1} max={31} value={value.monthDay}
                       onChange={(v) => set({ monthDay: Number(v) || 1 })} aria-describedby={`${idPrefix}-month-help`} />
          <p id={`${idPrefix}-month-help`} className="rp-help">{t('insights.reports.editor.monthDayHelp')}</p>
        </div>
      )}

      {value.frequency !== 'none' ? (
        <>
          <div className="rp-pair">
            <div>
              <label className="rp-label" htmlFor={`${idPrefix}-time`}>{t('insights.reports.editor.time')}</label>
              <TimePicker id={`${idPrefix}-time`} format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false}
                          value={dayjs(`2026-01-01T${value.runTime}`)}
                          onChange={(v) => v && set({ runTime: v.format('HH:mm') })} />
            </div>
            {zone && <p className="rp-help rp-help--end">{t('insights.reports.editor.zone', { zone })}</p>}
          </div>
          <div>
            <span className="rp-label">{nextRunsLabel}</span>
            <ul className="rp-runs" aria-live="polite" data-testid={`${idPrefix}-next-runs`}>
              {(nextRuns || []).map((run) => (
                <li key={run.at}>
                  <span>{instantLabel(run.at, zone || undefined)}</span>
                  {runDetail && <span className="rp-runs__covers">{runDetail(run)}</span>}
                </li>
              ))}
            </ul>
            <p className="rp-help">{timingNote}</p>
          </div>
        </>
      ) : <p className="rp-help">{byHandNote}</p>}
    </div>
  );
}
